(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const storageKey = 'ssb-pending-build-v1';
  let busy = false;
  let memoryPending = '';
  const pending = () => {
    try { return sessionStorage.getItem(storageKey) || memoryPending; }
    catch (_) { return memoryPending; }
  };
  const remember = (id) => {
    memoryPending = id || '';
    try {
      if (id) sessionStorage.setItem(storageKey, id);
      else sessionStorage.removeItem(storageKey);
    } catch (_) {}
  };
  const request = async (path, body) => {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'The cart could not be verified. Please try again.');
    return data;
  };
  const read = () => request('cart.js');
  const lines = (cart, id) => (cart.items || []).filter((item) => {
    const props = item.properties || {};
    return props['_Build Record'] === id || props['Build Record ID'] === id;
  });
  const complete = (cart, id, expected) => {
    const actual = lines(cart, id);
    if (actual.length !== expected.length) return false;
    return expected.every((part) => actual.some((item) =>
      String(item.variant_id || item.id) === String(part.id) &&
      item.quantity === (part.quantity || 1) &&
      (!part.key || (item.properties || {})['_SSB Component Key'] === part.key) &&
      (!part.requirePreview || Boolean((item.properties || {})['Build Preview'])) &&
      (!part.requireArtwork || Boolean((item.properties || {})['_Advanced Artwork File']))
    ));
  };
  const rollback = async (id) => {
    if (!id) return;
    const cart = await read();
    const updates = Object.fromEntries(lines(cart, id).map((item) => [item.key, 0]));
    if (Object.keys(updates).length) await request('cart/update.js', { updates });
    if (lines(await read(), id).length) throw new Error('The incomplete build still needs to be removed.');
    if (pending() === id) remember('');
  };
  const transact = async (id, expected, write) => {
    if (busy) throw new Error('A build is already being added. Please wait.');
    busy = true;
    let started = false;
    try {
      const previous = pending();
      if (previous) await rollback(previous);
      const before = await read();
      if (complete(before, id, expected)) return { cart: before, payload: null, existing: true };
      if (lines(before, id).length) await rollback(id);
      remember(id);
      started = true;
      const payload = await write();
      const cart = await read();
      if (!complete(cart, id, expected)) throw new Error('The complete build could not be verified in the cart.');
      remember('');
      return { cart, payload, existing: false };
    } catch (error) {
      if (started) {
        try { await rollback(id); }
        catch (_) {
          const blocked = new Error('The connection was interrupted. Your build needs cart recovery before checkout. Reconnect and try again, or open the cart to remove this build.');
          blocked.code = 'cart_recovery_required';
          throw blocked;
        }
      }
      throw error;
    } finally { busy = false; }
  };
  // Read-only checkout gate: incomplete made-to-order component groups cannot
  // silently pass through direct checkout or survive a page reload as valid.
  const ensureReady = async () => {
    if (busy || pending()) throw new Error('A custom build is still being recovered. Reload the cart and try again before checkout.');
    const cart = await read();
    if (!Array.isArray(cart.items) || !cart.items.length) throw new Error('Your cart is empty. Add a piece before checkout.');
    const groups = new Map();
    for (const item of cart.items) {
      const p = item.properties || {};
      const id = p['_Build Record'] || p['Build Record ID'];
      if (!id || !p['_SSB Component Key']) continue;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }
    for (const [id, items] of groups) {
      const primary = items.find((item) => (item.properties || {})['_SSB Component Key'] === 'instrument');
      if (!primary || primary.quantity !== 1 || !(primary.properties || {})['Build Preview']) {
        throw new Error('Custom build ' + id + ' is incomplete. Please use cart recovery before checkout.');
      }
      const props = primary.properties || {};
      if (String(props['_Artwork Transfer'] || '').includes('Original upload attached') && !props['_Advanced Artwork File']) {
        throw new Error('The original engraving artwork for ' + id + ' is missing. Rebuild this commission before checkout.');
      }
      const recorded = String(props['_SSB Expected Component Keys'] || '');
      if (recorded) {
        const expected = recorded.split('|').filter(Boolean);
        const found = items.map((item) => (item.properties || {})['_SSB Component Key']);
        if (expected.length !== found.length || new Set(expected).size !== expected.length ||
          expected.some((key) => found.filter((got) => got === key).length !== 1) ||
          items.some((item) => item.quantity !== 1)) {
          throw new Error('A paid component from custom build ' + id + ' is missing or duplicated. Rebuild before checkout.');
        }
      }
    }
    return cart;
  };
  const notify = () => {
    let el = document.getElementById('SSBCartRecovery');
    if (!el) {
      el = document.createElement('div');
      el.id = 'SSBCartRecovery';
      el.setAttribute('role', 'alert');
      Object.assign(el.style, { position: 'fixed', left: '12px', right: '12px', bottom: '12px', zIndex: '2147482001', padding: '16px', background: '#000', color: '#fff', border: '1px solid #b87333', maxWidth: '720px', margin: 'auto' });
      document.body.appendChild(el);
    }
    el.textContent = 'An interrupted build must be recovered before checkout. ';
    const link = document.createElement('a');
    link.href = root + 'cart';
    link.textContent = 'Review cart';
    link.style.color = '#d8a876';
    el.appendChild(link);
  };
  document.addEventListener('click', (event) => {
    if (!busy && !pending()) return;
    const target = event.target.closest?.('[name="checkout"], [data-ssb-checkout-button], [data-ssb-direct-checkout], a[href*="/checkout"]');
    if (!target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    notify();
  }, true);
  document.addEventListener('submit', (event) => {
    if (!busy && !pending()) return;
    if (!event.target.matches('form[action*="/cart"]') || event.target.matches('form[action*="/cart/add"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    notify();
  }, true);
  window.SSBBuildCart = { transact, rollback, read, complete, ensureReady };
  if (pending()) {
    busy = true;
    rollback(pending()).then(() => {
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) window.location.reload();
    }).catch(notify).finally(() => { busy = false; });
  }
})();
